/**
 * 地图图例组件
 *
 * 职责：
 * 1. 以可折叠形式展示各类动态地图（封路、绕行、禁停、重点关注区等）的颜色图例
 * 2. 使用与地图多边形一致的填充色与边框色，保证视觉一致性
 *
 * 布局：固定在地图右下角，展开/收起通过 CSS transition 实现平滑动画
 */
import React, { useMemo } from 'react';
import { AnchorLayerType, AnchorLayerText, LightMapAssistType, LightMapAssistTypeText } from '@/utils/constant';
import { ANCHOR_LAYER_STYLE } from '@/map-core';

interface LegendItemConfig {
  type: string;
  label: string;
  borderColor: string;
  fillColor: string;
}

/** 不参与图例的父类型（其子类型单独展示） */
const LEGEND_EXCLUDE = new Set<string>([AnchorLayerType.LD_ASSIST]);

/** 从统一样式配置自动生成图例项，排除父类型 */
const MAIN_LEGEND_CONFIG: LegendItemConfig[] = Object.values(AnchorLayerType)
  .filter((type) => !LEGEND_EXCLUDE.has(type))
  .map((type) => ({
    type,
    label: AnchorLayerText.get(type) ?? type,
    borderColor: ANCHOR_LAYER_STYLE[type].legendBorderColor,
    fillColor: ANCHOR_LAYER_STYLE[type].legendFillColor,
  }));

/** 轻图测试子类型图例项（样式独立维护，不走 ANCHOR_LAYER_STYLE） */
const LIGHT_MAP_LEGEND_CONFIG: LegendItemConfig[] = [
  {
    type: LightMapAssistType.BUFFER_ZONE,
    label: LightMapAssistTypeText.get(LightMapAssistType.BUFFER_ZONE) ?? '',
    borderColor: '#52c41a',
    fillColor: 'rgba(82,196,26,0.35)',
  },
  {
    type: LightMapAssistType.PARKING_ZONE,
    label: LightMapAssistTypeText.get(LightMapAssistType.PARKING_ZONE) ?? '',
    borderColor: '#597ef7',
    fillColor: 'rgba(89,126,247,0.35)',
  },
  {
    type: LightMapAssistType.WARNING_ZONE,
    label: LightMapAssistTypeText.get(LightMapAssistType.WARNING_ZONE) ?? '',
    borderColor: '#f5222d',
    fillColor: 'rgba(245,34,45,0.35)',
  },
];

const LEGEND_CONFIG = [...MAIN_LEGEND_CONFIG, ...LIGHT_MAP_LEGEND_CONFIG];

const buildGradient = (color: string) =>
  `linear-gradient(135deg, transparent 5px, ${color} 0)`;

interface LegendItemProps {
  config: LegendItemConfig;
  isLast: boolean;
}

/** 单个图例项 */
const LegendItem = React.memo(function LegendItem({ config, isLast }: LegendItemProps) {
  const boxStyle = useMemo(
    () => ({
      background: buildGradient(config.borderColor),
    }),
    [config.borderColor]
  );

  const innerStyle = useMemo(
    () => ({
      background: buildGradient(config.fillColor),
    }),
    [config.fillColor]
  );

  return (
    <div className="map-editor-legend-item">
      <div className="map-editor-legend-item-box" style={boxStyle}>
        <div className="map-editor-legend-item-box-inner" style={innerStyle} />
      </div>
      <span className="map-editor-legend-item-text">{config.label}</span>
      {!isLast && <div className="map-editor-legend-item-separator" />}
    </div>
  );
});

const Legend = () => {
  const [isOpen, setIsOpen] = React.useState(true);

  return (
    <div className={`map-legend ${isOpen ? 'map-legend--open' : 'map-legend--closed'}`}>
      {/* 展开状态：显示图例内容 + 收起按钮 */}
      {isOpen && (
        <div className="legend-content">
          <div className="map-editor-legend-items">
            {LEGEND_CONFIG.map((item, index) => {
              const isLast = index === LEGEND_CONFIG.length - 1;
              return <LegendItem config={item} isLast={isLast} key={item.type} />;
            })}
          </div>
          <i
            className="map-editor-legend-btn map-editor-icon-right"
            onClick={() => setIsOpen(false)}
          />
        </div>
      )}

      {/* 收起状态：只显示展开按钮 */}
      {!isOpen && (
        <i
          className="map-editor-legend-btn map-editor-icon-left"
          onClick={() => setIsOpen(true)}
        />
      )}
    </div>
  );
};

export default React.memo(Legend);
